import { z } from 'zod'
import { FirestoreTimestamp, TimeString } from './utils'

// Base callback schema with all fields
// Times are optional to allow FileMaker imports with null values
export const BaseCallbackSchema = z.object({
  // Core callback data
  date: z.iso.date(),
  timeArrived: TimeString.optional(),
  timeDeparted: TimeString.optional(),

  // Detailed information
  entrapment: z.boolean().default(false),
  runningOnArrival: z.boolean().default(false),
  problemDescription: z.string().min(1, 'Problem description is required'),
  problemType: z.string().min(1, 'Problem type is required'),
  workPerformed: z.string(),
  workOrder: z.string().nullable().default(null),
  // Media and documentation
  photos: z.array(z.string()).default([]),

  // Technician information
  completedBy: z.string().optional(),
  completedByUserId: z.string().optional(), // Optional link to actual user document

  // Export tracking (for OSU API integration)
  hasBeenUploaded: z.boolean().default(false),
})

// Full callback schema (for database documents)
export const Callback = BaseCallbackSchema.extend({
  companyId: z.string(),
  siteId: z.string(),
  unitId: z.string(),
  createdAt: FirestoreTimestamp,
  updatedAt: FirestoreTimestamp,
})

// Create callback schema - requires all fields except auto-generated ones
// Times are REQUIRED for UI submissions (not optional like in base schema)
export const CreateCallbackSchema = BaseCallbackSchema.extend({
  // Required fields for creation
  unitId: z.string().min(1, 'Unit ID is required'),
  siteId: z.string().min(1, 'Site ID is required'),
  date: z.iso.date(),
  timeArrived: TimeString,
  timeDeparted: TimeString,
}).partial({
  // Make optional fields explicitly partial
  photos: true,
  completedBy: true,
  completedByUserId: true,
  hasBeenUploaded: true, // Server-side tracking field, not required from client
})

// Edit work order schema - for updating work order field only
export const EditWorkOrderSchema = z.object({
  workOrder: z.string().nullable().default(null),
})

export type Callback = z.infer<typeof Callback>
export type CreateCallbackData = z.infer<typeof CreateCallbackSchema>
export type EditWorkOrderData = z.infer<typeof EditWorkOrderSchema>
